import React, { useEffect, useContext, useCallback } from 'react';
import { PAGED_CHECKLISTS_QUERY } from '../queries';
import { CHECKLIST_DELETED } from '../subscriptions';
import { ServiceContext } from 'providers/ServiceProvider';
import ExportAction from './actions/ExportAction';
import { useTranslation } from 'react-i18next';
import { Card } from '@totalsoft/rocket-ui';
import { useApolloClient } from '@apollo/client';
import { Origination } from 'constants/contexts';
import { getListBrowserStore, useListBrowserUtils } from '@ts-private/list-browser';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { useToast } from 'hooks/toasts/useToast';
import { useMemo } from 'react';
import TableListBrowser from 'features/common/components/table-list-browser/TableListBrowser';
import ApolloFetchingComponent from 'features/common/components/apollo-fetching/ApolloFetchingComponent';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import ChecklistItem from './ChecklistItem';
import { useMutation } from 'hooks/apollo/useMutation';
import { DELETE_CHECKLISTS } from '../mutations';
import useSubscription from 'hooks/apollo/useSubscription';

export const checklistStore = getListBrowserStore({
  filterDefinitions: [
    {
      name: 'documentName',
      caption: 'Checklist.DocumentName',
      type: 'text'
    }
  ],
  orderByFields: [
    {
      caption: 'Checklist.DocumentName',
      value: 'DocumentName'
    }
  ],
  pager: {
    orderBy: 'DocumentName',
    direction: 'asc'
  }
});

const ChecklistListBrowser = () => {
  const [t] = useTranslation();
  const addToast = useToast();
  const client = useApolloClient();
  const confirm = useConfirmDialog();
  const [activeService] = useContext(ServiceContext);
  const { addActions } = useListBrowserUtils(checklistStore);

  const fetchingOptions = useMemo(
    () => ({
      queryOptions: {
        query: PAGED_CHECKLISTS_QUERY,
        name: 'pagedChecklists'
      }
    }),
    []
  );

  // #region Delete

  useSubscription(CHECKLIST_DELETED, {
    onData: () => {
      addToast(t('Checklist.ChecklistRemoved'), 'success');
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedChecklists' });
      client.cache.gc();
    }
  });

  const [deleteChecklist] = useMutation(DELETE_CHECKLISTS);

  const handleDelete = useCallback(
    requiredDocumentId => {
      confirm(
        () => {
          deleteChecklist({ variables: { input: { requiredDocumentId } } });
        },
        {
          content: t('Checklist.DeleteChecklist')
        }
      );
    },
    [deleteChecklist, confirm, t]
  );

  // #endregion

  useEffect(() => {
    if (activeService) {
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedChecklists' });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'targetAreas' });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'requiredDocTypes' });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'genericEntityFlowsDictionary' });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'genericEntityBusinessStatesXFlow' });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'checklist' });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'checklistProperties' });
      client.cache.gc();
    }
  }, [activeService, client]);

  useEffect(() => {
    if (activeService?.route === Origination.serviceRoute)
      addActions([
        {
          name: 'export',
          icon: <ExportAction />
        }
      ]);
  }, [activeService?.route, addActions, t]);

  const itemComponent = useCallback(({ data }) => <ChecklistItem data={data} onDelete={handleDelete} />, [handleDelete]);

  const columns = useMemo(
    () => [
      { name: 'documentName', caption: 'Checklist.DocumentName' },
      { name: 'targetAreaName', caption: 'Checklist.TargetAreaName' },
      { name: 'documentCode', caption: 'Checklist.DocumentCode' },
      { name: 'sharePointContentType', caption: 'Checklist.SharePointContentType' },
      { name: 'mandatoryYN', caption: 'Checklist.MandatoryYN', width: '120px', align: 'center' },
      { name: 'defaultYN', caption: 'Checklist.DefaultYN', width: '100px', align: 'center' },
      { name: 'actions', caption: 'Actions', width: '120px' }
    ],
    []
  );

  return (
    <Card icon={ListAltIcon}>
      <TableListBrowser name='checklist-browser' store={checklistStore} ItemComponent={itemComponent} columns={columns} />
      <ApolloFetchingComponent store={checklistStore} options={fetchingOptions} />
    </Card>
  );
};

export default ChecklistListBrowser;
